import { useEffect, useMemo } from "react";

import qs from "query-string";
import styled from "styled-components";

import { Table } from "@similarweb/pro-logic-components";
import { Box } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { CellDropdownSWReactTableOnRowHover } from "app/pages/website-analysis/pages/components/CellSiteDropdown";
import { WithSegmentsLoading } from "app/pages/industry-analysis/frame/WithSegmentsLoading";

import { TABLE_CONFIG } from "../../constants";
import type { IFoldersItem, IFoldersTransformedData } from "../../types";
import { FoldersTableTop } from "../FoldersTableTop/FoldersTableTop";

import { FoldersTableColumns } from "./columnConfig";
import { useFoldersConfig } from "./hooks/useFoldersConfig";
import { useFoldersData } from "./hooks/useFoldersData";
import { useTableExpansion } from "./hooks/useTableExpansion";
import { useTableSelection } from "./hooks/useTableSelection/useTableSelection";

const TableWrapper = styled(Box)`
    width: 100%;
    max-width: 1368px;
    height: auto;
    border-radius: 6px;
    overflow: hidden;
    display: block;
    ${CellDropdownSWReactTableOnRowHover}

    div.ant-table-cell.ant-table-selection-column {
        justify-content: center;
    }

    .ant-table-tbody {
        .ant-table-row {
            div.ant-table-cell {
                display: flex;
                align-items: center;
            }

            .ant-table-cell-content {
                width: 100%;
            }
        }
    }
`;

const FoldersTable = () => {
    const config = useFoldersConfig();
    const {
        currentDomain,
        setCurrentDomain,
        filters,
        sortConfig,
        radioFilters,
        handleSort,
        handleFilterChange,
        tablePagination: configTablePagination,
        searchTerm,
    } = config;

    const { transformedData, isTableLoading } = useFoldersData(filters, searchTerm) as {
        transformedData: IFoldersTransformedData;
        isTableLoading: boolean;
    };

    const { expandedRowKeys, onExpandedRowsChange, rowExpandable, onExpand } = useTableExpansion(
        transformedData.data,
        !!filters.filter,
        { isNew: filters.isNew, isTrending: filters.isTrending },
    ) as {
        expandedRowKeys: string[];
        onExpandedRowsChange: (keys: readonly React.Key[]) => void;
        rowExpandable: (record: IFoldersItem) => boolean;
        onExpand: (expanded: boolean, record: IFoldersItem) => void;
    };

    const { setSelectedRowKeys, tableSelection, selectedFoldersForSegment } = useTableSelection(transformedData.data);

    useEffect(() => {
        setSelectedRowKeys([]);
    }, [filters.isAll, filters.isNew, filters.isTrending, currentDomain, setSelectedRowKeys]);

    const tablePagination = {
        ...configTablePagination,
        total: transformedData.totalTopLevelCount,
    };

    const columnConfig = useMemo(() => {
        return FoldersTableColumns.getColumns(
            transformedData.totalCount,
            tablePagination.current,
            tablePagination.pageSize,
            sortConfig,
        );
    }, [transformedData.totalCount, tablePagination.current, tablePagination.pageSize, sortConfig]);
    const excelLink = `widgetApi/LeadingFolders/LeadingFolders/Excel?${qs.stringify(filters)}`;

    return (
        <TableWrapper>
            <Table
                key={`folders-table-${sortConfig.columnKey}-${sortConfig.order}-${tablePagination.current}`}
                data={transformedData.data}
                columnConfig={columnConfig}
                isLoading={isTableLoading}
                pagination={tablePagination}
                loaderRowCount={TABLE_CONFIG.LOADER_ROW_COUNT}
                noData={{
                    subtitle: translate("global.nodata.notavilable"),
                }}
                onSort={handleSort}
                expandable={{
                    expandIconColumnIndex: TABLE_CONFIG.EXPAND_ICON_COLUMN_INDEX,
                    onExpand,
                    expandedRowKeys,
                    onExpandedRowsChange,
                    rowExpandable,
                }}
                virtual={true}
                scroll={{ y: 1000 }}
                selection={tableSelection}
            >
                <FoldersTableTop
                    excelLink={excelLink}
                    initialFilters={filters}
                    radioFilters={radioFilters}
                    onFilterChange={handleFilterChange}
                    downloadExcelPermitted={true}
                    currentDomain={currentDomain}
                    setCurrentDomain={setCurrentDomain}
                    selectedFoldersForSegment={selectedFoldersForSegment}
                    searchTerm={searchTerm}
                />
            </Table>
        </TableWrapper>
    );
};

const FoldersTableWithSegmentsLoading = WithSegmentsLoading(FoldersTable);
export default FoldersTableWithSegmentsLoading;
